import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const inputCls = "bg-white/5 border border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60 transition-all duration-200 rounded-none";
const labelCls = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";

export default function DiscountCodeFormModal({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState({
    code: '', description: '', type: 'percentage', value: '',
    active: true, expiry_date: '', min_spend: '', max_uses: '', one_per_customer: false,
    value_products: '', value_experiences: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          code: editing.code || '',
          description: editing.description || '',
          type: editing.type || 'percentage',
          value: String(editing.value ?? ''),
          active: editing.active !== false,
          expiry_date: editing.expiry_date || '',
          min_spend: editing.min_spend ? String(editing.min_spend) : '',
          max_uses: editing.max_uses ? String(editing.max_uses) : '',
          one_per_customer: !!editing.one_per_customer,
          value_products: editing.value_products != null ? String(editing.value_products) : '',
          value_experiences: editing.value_experiences != null ? String(editing.value_experiences) : '',
        });
      } else {
        setForm({
          code: '', description: '', type: 'percentage', value: '',
          active: true, expiry_date: '', min_spend: '', max_uses: '', one_per_customer: false,
        });
      }
      setError('');
    }
  }, [open, editing]);

  const handleSave = async () => {
    if (!form.code.trim()) { setError('Code is required.'); return; }
    if (!form.value || Number(form.value) <= 0) { setError('Value must be greater than 0.'); return; }
    if (form.type === 'percentage' && Number(form.value) > 100) { setError('Percentage cannot exceed 100.'); return; }

    setSaving(true);
    setError('');
    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      type: form.type,
      value: Number(form.value),
      active: form.active,
      expiry_date: form.expiry_date || null,
      min_spend: form.min_spend ? Number(form.min_spend) : 0,
      max_uses: form.max_uses ? Number(form.max_uses) : 0,
      one_per_customer: form.one_per_customer,
      ...(form.type === 'percentage'
        ? {
            value_products: form.value_products ? Number(form.value_products) : null,
            value_experiences: form.value_experiences ? Number(form.value_experiences) : null,
          }
        : { value_products: null, value_experiences: null }),
    };

    try {
      if (editing) {
        await base44.entities.DiscountCode.update(editing.id, payload);
      } else {
        await base44.entities.DiscountCode.create({ ...payload, used_count: 0 });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not save discount code.');
    }
    setSaving(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#0d0d0f] border border-white/10"
          >
            <div className="h-1 bg-primary" />
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-white font-black text-lg uppercase tracking-tight">
                  {editing ? 'Edit Discount Code' : 'New Discount Code'}
                </h2>
                <button onClick={onClose} className="text-white/30 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>CODE *</label>
                    <input
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                      className={inputCls + ' w-full px-3 font-mono'}
                      placeholder="SUMMER10"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>TYPE *</label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}
                      className={inputCls + ' w-full px-3'}
                    >
                      <option value="percentage" className="bg-[#1a1a1f]">Percentage (%)</option>
                      <option value="fixed" className="bg-[#1a1a1f]">Fixed (£)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{form.type === 'percentage' ? 'PERCENTAGE *' : 'AMOUNT £ *'}</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form.value}
                      onChange={(e) => setForm({ ...form, value: e.target.value })}
                      className={inputCls + ' w-full px-3'}
                      placeholder={form.type === 'percentage' ? '10' : '25.00'}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>MIN SPEND £</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={form.min_spend}
                      onChange={(e) => setForm({ ...form, min_spend: e.target.value })}
                      className={inputCls + ' w-full px-3'}
                      placeholder="0"
                    />
                  </div>
                </div>

                {form.type === 'percentage' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>PRODUCTS % (OPTIONAL)</label>
                      <input
                        type="number" step="0.01" min="0" max="100"
                        value={form.value_products}
                        onChange={(e) => setForm({ ...form, value_products: e.target.value })}
                        className={inputCls + ' w-full px-3'}
                        placeholder="Blank = use main %"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>EXPERIENCES % (OPTIONAL)</label>
                      <input
                        type="number" step="0.01" min="0" max="100"
                        value={form.value_experiences}
                        onChange={(e) => setForm({ ...form, value_experiences: e.target.value })}
                        className={inputCls + ' w-full px-3'}
                        placeholder="Blank = use main %"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className={labelCls}>DESCRIPTION (ADMIN ONLY)</label>
                  <input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className={inputCls + ' w-full px-3'}
                    placeholder="Summer promo 10% off"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>EXPIRY DATE</label>
                    <input
                      type="date"
                      value={form.expiry_date}
                      onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                      className={inputCls + ' w-full px-3'}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>MAX USES (0 = UNLIMITED)</label>
                    <input
                      type="number" min="0"
                      value={form.max_uses}
                      onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                      className={inputCls + ' w-full px-3'}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6 pt-1">
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <input type="checkbox" checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      className="w-4 h-4 accent-primary" />
                    <span className="text-white/50 text-xs group-hover:text-white/70 transition-colors">Active</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <input type="checkbox" checked={form.one_per_customer}
                      onChange={(e) => setForm({ ...form, one_per_customer: e.target.checked })}
                      className="w-4 h-4 accent-primary" />
                    <span className="text-white/50 text-xs group-hover:text-white/70 transition-colors">One per customer</span>
                  </label>
                </div>

                {error && (
                  <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-3 py-2">{error}</p>
                )}

                <div className="flex gap-3 pt-2">
                  <button onClick={onClose}
                    className="flex-1 border border-white/10 text-white/50 hover:text-white font-bold text-xs tracking-widest py-3 transition-colors">
                    CANCEL
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest py-3 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (editing ? 'SAVE CHANGES' : 'CREATE CODE')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}